import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './change-dates-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';

export interface ChangeDatesModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    reservation: ReservationDetailsUiModel | undefined;
    isLoading: boolean;
    errorMessage?: string;
  };
  eventHandlers: {
    closeHandler: () => void;
    submitHandler: (checkInDate: number, checkOutDate: number) => void;
  };
}

/**
 * functional component ChangeDatesModal
 */
export const ChangeDatesModal: React.FC<ChangeDatesModalProps> = ({
  config,
  data,
  eventHandlers
}) => {
  const isMobileView = useWindowResizer();

  const [checkInDate, setCheckInDate] = useState<Date | null>(
    data.reservation ? new Date(data.reservation.checkInDate) : null
  );
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(
    data.reservation ? new Date(data.reservation.checkOutDate) : null
  );

  const isValid = checkInDate !== null && checkOutDate !== null && checkInDate < checkOutDate;

  const handleSubmit = () => {
    if (checkInDate && checkOutDate) {
      eventHandlers.submitHandler(checkInDate.getTime(), checkOutDate.getTime());
    }
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true, maxWidth: 'sm' }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['change-dates-modal']}>
        {/* Header */}
        <div className={styles['change-dates-modal__header']}>
          <h2 className={styles['change-dates-modal__title']}>Change Dates</h2>
          <p className={styles['change-dates-modal__subtitle']}>
            Select new check-in and check-out dates for this reservation.
          </p>
        </div>

        <div className={styles['change-dates-modal__warning']}>
          <span className={styles['change-dates-modal__warning-icon']}>&#9888;</span>
          Changing dates may affect availability and pricing.
        </div>

        {/* Current Dates */}
        {data.reservation && (
          <div className={styles['change-dates-modal__current']}>
            <div className={styles['change-dates-modal__current-label']}>Current Dates</div>
            <div className={styles['change-dates-modal__current-dates']}>
              <span>{new Date(data.reservation.checkInDate).toDateString()}</span>
              <span className={styles['change-dates-modal__arrow-icon']}>&#8594;</span>
              <span>{new Date(data.reservation.checkOutDate).toDateString()}</span>
            </div>
          </div>
        )}

        {/* Date Pickers */}
        <div className={styles['change-dates-modal__content']}>
          <div className={styles['change-dates-modal__field']}>
            <label className={styles['change-dates-modal__label']}>
              Check-in Date <span className={styles['change-dates-modal__required']}>*</span>
            </label>
            <ReactDatePicker
              selected={checkInDate}
              onChange={(date: Date) => {
                setCheckInDate(date);
                if (checkOutDate && date >= checkOutDate) {
                  setCheckOutDate(null);
                }
              }}
              selectsStart
              startDate={checkInDate}
              endDate={checkOutDate}
              placeholderText="Select check-in date"
              dateFormat="dd/MM/yyyy"
              className={styles['change-dates-modal__datepicker']}
              wrapperClassName={styles['change-dates-modal__datepicker-wrapper']}
              popperPlacement="bottom-start"
            />
          </div>

          <div className={styles['change-dates-modal__field']}>
            <label className={styles['change-dates-modal__label']}>
              Check-out Date <span className={styles['change-dates-modal__required']}>*</span>
            </label>
            <ReactDatePicker
              selected={checkOutDate}
              onChange={(date: Date) => setCheckOutDate(date)}
              selectsEnd
              startDate={checkInDate}
              endDate={checkOutDate}
              minDate={checkInDate ? new Date(checkInDate.getTime() + 86400000) : undefined}
              placeholderText="Select check-out date"
              dateFormat="dd/MM/yyyy"
              className={styles['change-dates-modal__datepicker']}
              wrapperClassName={styles['change-dates-modal__datepicker-wrapper']}
              popperPlacement="bottom-start"
              disabled={!checkInDate}
            />
          </div>
        </div>

        {/* Inline Error */}
        {data.errorMessage && (
          <div className={styles['change-dates-modal__error']}>
            <span className={styles['change-dates-modal__error-icon']}>&#9888;</span>
            {data.errorMessage}
          </div>
        )}

        {/* Actions */}
        <div className={styles['change-dates-modal__actions']}>
          <Button
            onClick={() => eventHandlers.closeHandler()}
            type={ButtonType.primaryInverted}
            tracking={{
              action: 'close',
              category: 'reservation',
              label: 'change-dates-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            loading={data.isLoading}
            disabled={!isValid || data.isLoading}
            onClick={handleSubmit}
            tracking={{
              action: 'submit',
              category: 'reservation',
              label: 'change-dates-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            {data.isLoading ? 'Checking...' : 'Update Dates'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
